import React, { useState, useEffect, useRef, useCallback } from 'react'
import { SearchIcon, PlusIcon, MinusIcon, SettingsIcon } from '@icons/utils'

import styles from '../LearnerTeamMembers.module.scss'
import sharedStyles from "@src/pages/MultiPage.module.scss"
import { LxIcon } from '@components/icon/Icon.tsx'
import { cn } from '@src/utils/cn.ts'
import { useLearnerShow } from '@logic/contexts/Learners/LearnerShowContext.tsx'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import { mapRoleToReadableRole } from '@src/utils/mapRoleToReadableRole.ts'
import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import { AddNewLernerTeamMember } from '@src/pages/Learner/Show/TeamMembers/LearnerAddNewTeamMember.tsx'
import { UserF } from '@logic/contexts/Users/UsersFrontend.type.ts'
import { ConfirmationModal } from '@src/utils/confirmationModel'
import { LearnerMemberEditAccess } from '@src/pages/Learner/Show/TeamMembers/LearnerMemberEditAccess.tsx'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { InfoModal } from '@components/modal/InfoModal'
import { useSubmitLoader } from '@src/logic/contexts/utils/SubmitContext.tsx'
import { LxHandleKeyDown } from '@src/utils/Accessbility'

import { useDataProvider, useNotify } from 'react-admin'
import { isNilOrEmpty } from '@src/utils/isNilOrEmpty.ts'
import {
  CAN_ASSIGN_LEARNER_STUDENT_TO_OTHERS,
  CAN_EDIT_DELETE_ALL_GOALS_OF_LEARNER,
  CAN_RUN_ALL_ACTIVITIES_OF_LEARNER,
  CAN_VIEW_ALL_GOALS_OF_LEARNER,
} from '@logic/contexts/AppStore/UserPermissions.constant.ts'

export const UserAssignOtherUserView = ({ currentUser }) => {
  const { learner, isLoading } = useLearnerShow()
  const learnerId = learner?.id
  const currentUserId = currentUser.id
  const [openAccessModal, setOpenAccessModal] = useState(false)
  const { showModal, hideModal } = useModal()

  const dataProvider = useDataProvider()
  const notify = useNotify()

  const [assignedUserData, setAssignedUserData] = useState<User[]>([])
  const [isAssignedUserDataLoading, setIsAssignedUserDataLoading] = useState(true)
  const [userData, setUserData] = useState<User[]>([])
  const [isUserDataLoading, setIsUserDataLoading] = useState(true)
  const [filteredAssignedUserData, setFilteredAssignedUserData] = useState()

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [unassignParams, setUnassignParams] = useState({
    userId: null,
    superVisorId: null,
  })
  const [selectedUser, setSelectedUser] = useState({})
  const [refreshKey, setRefreshKey] = useState(0)

  const { isFormSubmitting, setIsFormSubmitting } = useSubmitLoader()

  const fullName = (user) => {
    return `${user.first_name} ${user.last_name}`
  }

  // get all users and details

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dataProvider.getList('User', {
          pagination: { page: 0, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
          filter: { state: 'active', list: 'all_users_basic_details' },
        })

        const userDataSubset: User[] = []

        response.data.forEach((user: any) => {
          const userDataItem: User = {
            id: user.id,
            fullName: fullName(user),
            userRole: mapRoleToReadableRole(user.user_role),
          }

          userDataSubset.push(userDataItem)
        })

        setUserData(userDataSubset)
        setIsUserDataLoading(false)
      } catch (error) {
        // Handle error
        setIsUserDataLoading(false)
      }
    }
    fetchData()
  }, [dataProvider])

  // get assigned users and details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dataProvider.getList('User', {
          pagination: { page: 0, perPage: 100 },
          sort: { field: 'id', order: 'ASC' },
          filter: { list: 'learnerUsers', learner_id: learnerId },
        })

        const assignedUsersArray = []

        const userDataMap = userData.reduce((acc, user) => {
          acc[user.id] = {
            fullName: user.fullName,
            userRole: user.userRole,
          }
          return acc
        }, {})

        Object.entries(response.data || {}).map(([_, userData]) => {
          const userDict = {
            [CAN_VIEW_ALL_GOALS_OF_LEARNER]: false,
            [CAN_EDIT_DELETE_ALL_GOALS_OF_LEARNER]: false,
            id: userData.id,
            fullName: userDataMap[userData.id]?.fullName,
            userRole: userDataMap[userData.id]?.userRole,
            assistants: [],
          }
          const permissionsToCheck = [CAN_VIEW_ALL_GOALS_OF_LEARNER, CAN_EDIT_DELETE_ALL_GOALS_OF_LEARNER, CAN_ASSIGN_LEARNER_STUDENT_TO_OTHERS]

          permissionsToCheck.forEach((permission) => {
            if (userData.assigned_learners[learnerId].includes(permission)) {
              userDict[permission] = true
            }
          })

          Object.entries(userData.assigned_users || {}).map(([key, assistantData]) => {
            if (learnerId in assistantData.learners) {
              userDict.assistants.push({
                [CAN_RUN_ALL_ACTIVITIES_OF_LEARNER]: assistantData.learners[learnerId].includes(CAN_RUN_ALL_ACTIVITIES_OF_LEARNER),
                id: key,
                fullName: userDataMap[key]?.fullName,
                userRole: userDataMap[key]?.userRole,
                supervisorId: userData.id,
                supervisorFullName: userDataMap[userData.id].fullName,
              })
            }
          })
          assignedUsersArray.push(userDict)
        })

        // Mapping assignedUserData with assistants details
        assignedUsersArray.forEach((user) => {
          if (user.assistants.length > 0) {
            user.assistants.forEach((assistant) => {
              const assistantWithSupervisorId = {
                ...assistant,
              }
              assignedUsersArray.push(assistantWithSupervisorId)
            })

            delete user.assistants
          }
        })

        setAssignedUserData(assignedUsersArray)
        setFilteredAssignedUserData(assignedUsersArray)
        setIsAssignedUserDataLoading(false)
      } catch (error) {
        // Handle error
        setIsAssignedUserDataLoading(false)
      }
    }
    if (learnerId) {
      fetchData()
    }
  }, [userData, refreshKey])

  const handleSearchInputChange = (inputValue) => {
    const filteredUsers = assignedUserData.filter((user) => user.fullName.toLowerCase().includes(inputValue.toLowerCase()))
    setFilteredAssignedUserData(filteredUsers)
  }

  // getting unassigned users
  const assignedUserIds = new Set(assignedUserData.map((user) => user.id))

  const handleAccessEdit = async (user) => {
    setSelectedUser(user)
    setOpenAccessModal(true)
  }

  const handleUnAssign = async (userId: string, superVisorId: string) => {
    try {
      setUnassignParams({ userId, superVisorId })
      setIsConfirmationOpen(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
      setRefreshKey((prevKey) => prevKey + 1)
    }
  }

  const handleConfirmUnassign = async () => {
    if (isFormSubmitting) {
      return
    }

    setIsFormSubmitting(true)
    try {
      const { userId, superVisorId } = unassignParams
      await dataProvider.update('Learner', {
        id: learnerId,
        data: {
          assign_user: {
            user_id: userId,
            supervisor_id: superVisorId,
            action: 'remove_user',
          },
        },
        previousData: null,
      })
      notify('User is removed successfully', { type: 'success' })
    } catch (error) {
      const errorMessage = error.message || 'Something went wrong, Please try again after sometime'
      notify(errorMessage, { type: 'error' })
    } finally {
      setRefreshKey((prevKey) => prevKey + 1)
      setIsFormSubmitting(false)
      setIsConfirmationOpen(false)
    }
  }

  const modalRef = useRef<HTMLDivElement>(null)

  const renderModal = useCallback(() => {
    showModal(<AddNewLernerTeamMember onClose={hideModal} users={userData} learnerId={learnerId} learnerFullName={learner?.fullName} setRefreshKey={setRefreshKey} assignedUserIds={assignedUserIds} canCurrentUserViewGoals={canCurrentUserViewGoals} canCurrentUserEditGoals={canCurrentUserEditGoals} />)

    setTimeout(() => {
      modalRef.current?.focus()
    }, 0) // Ensures model is focused after it is rendered
  }, [showModal, userData, learnerId, setRefreshKey, assignedUserIds])

  const permissionRestrictMessage =
    'Sorry, your role allows assigning learners to others, but you cannot unassign or change permissions except for your own assistants.Please contact an Admin for any additional actions.'

  // Determine current user's permissions over this learner
  const currentUserLearnerAccess = assignedUserData.find(
    (u) => u.id === currentUserId
  ) || {};

  const canCurrentUserViewGoals = currentUserLearnerAccess[CAN_VIEW_ALL_GOALS_OF_LEARNER] || false;
  const canCurrentUserEditGoals = currentUserLearnerAccess[CAN_EDIT_DELETE_ALL_GOALS_OF_LEARNER] || false;

  return (
    <>
      <div className={styles.studentTeamMembersNavigation}>
        {isLoading || isAssignedUserDataLoading || isUserDataLoading ? (
          <LxLoadingSpinner />
        ) : (
          <>
            <div className={cn(styles.membersInfo)}>
              <h3>Team Members Assigned to {learner?.fullName} </h3>
            </div>
            <div className={cn(sharedStyles.tableActions)}>
              <div
                className={cn('lxActionButton', 'lxActionButtonFilled lxActionButtonDefaultSize')}
                onClick={renderModal}
                onKeyDown={(event) => LxHandleKeyDown(event, renderModal)}
                tabIndex={0}
                role='button'
                aria-label='Assign New Team Member'
              >
                <LxIcon icon={PlusIcon} />
                Assign New Team Member
              </div>
              <div className={'lxActionButton lxActionButtonDefaultSize'}>
                <LxIcon icon={SearchIcon} />
                <input
                  className={'pristineInput'}
                  type='text'
                  onChange={(e) => {
                    handleSearchInputChange(e.target.value)
                  }}
                  placeholder='Search'
                  role='search'
                  aria-label='Search'
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={styles.studentTeamMembersList}>
        <div className={cn(sharedStyles.tableListHeader)}> Team Member Name</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide2column)}> Role</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide3column)}> Supervisor</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide4column)}> Access Control</div>
        <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide5column)}> Unassign</div>
        <>
          {isAssignedUserDataLoading ? (
            <LxLoadingSpinner className={sharedStyles.loader} />
          ) : isNilOrEmpty(filteredAssignedUserData) ? (
            <div className={sharedStyles.notFound}>No team members match your current search or filters.</div>
          ) : (
            filteredAssignedUserData.map((user: UserF) => (
              <React.Fragment key={`User-row-${user.id}-${user.supervisorId}`}>
                <div>{user.fullName}</div>
                <div className={sharedStyles.hide2column}>{user.userRole}</div>
                <div className={sharedStyles.hide3column}>{user.supervisorFullName ? user.supervisorFullName : 'N/A'}</div>
                <div className={sharedStyles.hide4column}>
                  {user.userRole == 'Assistant' && user.supervisorId === currentUserId ? (
                    <LxClickAbleIcon
                      onClick={() => {
                        handleAccessEdit(user)
                      }}
                      icon={SettingsIcon}
                      role='button'
                      ariaLabel='Access Control'
                    />
                  ) : (
                    <div>
                      <InfoModal icon={SettingsIcon} message={permissionRestrictMessage} header={`Permission Restricted`} ariaLabel={permissionRestrictMessage} />
                    </div>
                  )}
                </div>
                {user.userRole == 'Assistant' && user.supervisorId === currentUserId ? (
                  <LxClickAbleIcon
                    className={sharedStyles.hide5column}
                    onClick={() => {
                      handleUnAssign(user.id, user.supervisorId)
                    }}
                    icon={MinusIcon}
                    role='button'
                    ariaLabel='Un-assign User'
                  />
                ) : (
                  <div>
                    <InfoModal icon={MinusIcon} message={permissionRestrictMessage} header={`Permission Restricted`} ariaLabel={permissionRestrictMessage} />
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </>
      </div>

      <LearnerMemberEditAccess
        isModalOpen={openAccessModal}
        onClose={() => setOpenAccessModal(false)}
        user={selectedUser}
        learnerId={learnerId}
        learnerFullName={learner?.fullName}
        setRefreshKey={setRefreshKey}
        isFormSubmitting={isFormSubmitting}
        setIsFormSubmitting={setIsFormSubmitting}
      />

      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirmUnassign}
        message='Are you sure you want to unassign this user?'
        isFormSubmitting={isFormSubmitting}
      />
    </>
  )
}
